import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAccessTime,
  MdAdd,
  MdBarChart,
  MdBookmark,
  MdCalendarToday,
  MdChatBubbleOutline,
  MdDescription,
  MdEmail,
  MdFlashOn,
  MdGroup,
  MdHelpOutline,
  MdLabel,
  MdNotifications,
  MdSearch,
  MdSettings,
  MdShoppingCart,
  MdStar,
} from "react-icons/md";
import {
  TranslationTabs,
  TranslationValues,
  pruneEmptyTranslations,
} from "../../../../components/common/TranslationTabs";
import {
  Box,
  Button,
  Grid,
  HStack,
  Text,
  VStack,
} from "../../../../components/ui";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Field } from "../../../../components/ui/field";
import { Tooltip } from "../../../../components/ui/tooltip";
import {
  CreatePromptShortcutRequest,
  PromptShortcut,
  promptService,
  UpdatePromptShortcutRequest,
} from "../../../../services/promptService";
import { showToast } from "../../../../utils/toast";

// Same icon catalogue used by WidgetShortcutCard so the chip rendered in the
// widget matches what the admin picks here. Re-defined locally to keep this
// page decoupled from the welcome-screen card editor's internals.
const ICON_OPTIONS: {
  id: string;
  label: string;
  component: React.ElementType;
}[] = [
  { id: "zap", label: "Zap", component: MdFlashOn },
  { id: "bar-chart", label: "Chart", component: MdBarChart },
  { id: "help-circle", label: "Help", component: MdHelpOutline },
  { id: "message", label: "Chat", component: MdChatBubbleOutline },
  { id: "search", label: "Search", component: MdSearch },
  { id: "file-text", label: "Document", component: MdDescription },
  { id: "settings", label: "Settings", component: MdSettings },
  { id: "star", label: "Star", component: MdStar },
  { id: "calendar", label: "Calendar", component: MdCalendarToday },
  { id: "clock", label: "Clock", component: MdAccessTime },
  { id: "users", label: "Team", component: MdGroup },
  { id: "shopping-cart", label: "Cart", component: MdShoppingCart },
  { id: "mail", label: "Email", component: MdEmail },
  { id: "bell", label: "Alert", component: MdNotifications },
  { id: "bookmark", label: "Bookmark", component: MdBookmark },
  { id: "tag", label: "Tag", component: MdLabel },
];

interface PromptShortcutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  shortcut?: PromptShortcut | null;
}

const PromptShortcutModal: React.FC<PromptShortcutModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  shortcut,
}) => {
  const { t } = useTranslation();
  const isEdit = !!shortcut;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("");
  const [icon, setIcon] = useState<string>("zap");
  const [translations, setTranslations] = useState<
    Record<string, TranslationValues>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(shortcut?.name ?? "");
      setDescription(shortcut?.description ?? "");
      setBody(shortcut?.body ?? "");
      setIcon(shortcut?.icon ?? "zap");
      setTranslations(shortcut?.translations ?? {});
      setErrors({});
    }
  }, [isOpen, shortcut]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim())
      next.name = t("app.promptShortcuts.modal.errors.nameRequired");
    if (!body.trim())
      next.body = t("app.promptShortcuts.modal.errors.bodyRequired");
    if (name.length > 255)
      next.name = t("app.promptShortcuts.modal.errors.nameTooLong");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      // Always sent, including as `{}` — that's the explicit "clear translations" signal
      // the API expects when the admin removes the last language.
      const prunedTranslations = pruneEmptyTranslations(translations);
      if (isEdit && shortcut) {
        const payload: UpdatePromptShortcutRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          body: body.trim(),
          icon,
          translations: prunedTranslations,
        };
        await promptService.updateCompanyShortcut(shortcut.id, payload);
        showToast({
          title: t("app.promptShortcuts.modal.editToast"),
          status: "success",
        });
      } else {
        const payload: CreatePromptShortcutRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          body: body.trim(),
          icon,
          translations: prunedTranslations,
        };
        await promptService.createCompanyShortcut(payload);
        showToast({
          title: t("app.promptShortcuts.modal.createToast"),
          status: "success",
        });
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast({
        title: t("app.promptShortcuts.modal.errors.saveFailed"),
        description:
          error?.response?.data?.message ?? error?.message ?? String(error),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
      size="lg"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? t("app.promptShortcuts.modal.editTitle")
                : t("app.promptShortcuts.modal.createTitle")}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <VStack align="stretch" gap={4}>
              {/* Icon sits below the tabs on purpose — it is language-neutral. */}
              <TranslationTabs
                fields={[
                  {
                    key: "name",
                    label: t("app.promptShortcuts.fields.name"),
                    type: "input",
                    required: true,
                    maxLength: 255,
                    placeholder: t(
                      "app.promptShortcuts.fields.namePlaceholder",
                    ),
                  },
                  {
                    key: "description",
                    label: t("app.promptShortcuts.fields.description"),
                    type: "input",
                    maxLength: 1000,
                    placeholder: t(
                      "app.promptShortcuts.fields.descriptionPlaceholder",
                    ),
                    helperText: t("app.promptShortcuts.fields.descriptionHelp"),
                  },
                  {
                    key: "body",
                    label: t("app.promptShortcuts.fields.body"),
                    type: "textarea",
                    required: true,
                    rows: 6,
                    placeholder: t(
                      "app.promptShortcuts.fields.bodyPlaceholder",
                    ),
                    helperText: t("app.promptShortcuts.placeholderHint"),
                  },
                ]}
                defaultValues={{ name, description, body }}
                onDefaultChange={(values) => {
                  setName(values.name ?? "");
                  setDescription(values.description ?? "");
                  setBody(values.body ?? "");
                }}
                translations={translations}
                onTranslationsChange={setTranslations}
                errors={errors}
              />

              <Field label={t("app.promptShortcuts.fields.icon")}>
                <Grid templateColumns="repeat(8, 1fr)" gap={1}>
                  {ICON_OPTIONS.map((opt) => {
                    const isSelected = icon === opt.id;
                    const Comp = opt.component;
                    return (
                      <Tooltip key={opt.id} content={opt.label}>
                        <Box
                          as="button"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            setIcon(opt.id);
                          }}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          w={8}
                          h={8}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={
                            isSelected
                              ? "var(--primary-color, #2563eb)"
                              : "var(--border-default)"
                          }
                          bg={
                            isSelected
                              ? "var(--primary-color-alpha, rgba(37,99,235,0.1))"
                              : "transparent"
                          }
                          color={
                            isSelected
                              ? "var(--primary-color, #2563eb)"
                              : "var(--text-secondary-color)"
                          }
                          cursor="pointer"
                          transition="all 0.15s"
                          aria-pressed={isSelected}
                          aria-label={opt.label}
                        >
                          <Comp size={16} />
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Grid>
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter>
            <HStack gap={2}>
              <DialogActionTrigger asChild>
                <Button intent="secondary" onClick={onClose} disabled={loading}>
                  {t("app.common.cancel")}
                </Button>
              </DialogActionTrigger>
              <Button intent="primary" onClick={handleSave} loading={loading}>
                {isEdit
                  ? t("app.common.save")
                  : t("app.promptShortcuts.modal.createCta")}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default PromptShortcutModal;
